import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { Product } from '../../../../modules/productsListing/types'
import { FormikProps, useFormikContext } from 'formik'
import { Dimension, PrivateOffer } from '../../../../modules/privateOffer/types'
import {
  BoxV4,
  InputStartAdornment,
  TextInputV4,
} from '@labrav/react-components'
import { Typography } from '@mui/material'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShelves } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './PrivateOfferInstances.styles'
import clsx from 'clsx'
import { getRandom } from '../../../../common/utils/helperFunctions'
import { get } from 'lodash'
import { getCurrencySymbol } from '../../../../common/utils/getCurrencySymbol'

export type PrivateOfferInstancesProps = {
  product: Product
}
export const PrivateOfferInstances: React.FC<
  PrivateOfferInstancesProps
> = () => {
  const { values, errors, touched, handleBlur, handleChange } =
    useFormikContext() as FormikProps<PrivateOffer>
  const dimensions = (values?.dimensions || []) as Dimension[]

  const getIsError = (path: string) => {
    return !!(get(touched, path, false) && get(errors, path, false))
  }
  const getErrorText = (path: string) => {
    return (!!get(touched, path, false) && get(errors, path, '')) || ''
  }
  const getInputKey = (isFlexible: boolean) => {
    return isFlexible ? 'quantity' : 'price'
  }
  const flexiblePaymentTerms = useMemo(() => {
    return values?.flexiblePaymentTerms as boolean
  }, [values?.flexiblePaymentTerms])
  const randomKey = useMemo(
    () => getRandom(),
    [dimensions.length, values?.flexiblePaymentTerms]
  )

  const dimensionsLength = dimensions.length
  const { classes } = useStyles({
    onlyUsagePricing: values?.pricingModel === 'usage-pricing',
  })

  const MAX_VISIBLE_ITEMS = 30
  const ITEMS_TO_ADD = 15
  const ITEMS_TO_REMOVE = 10

  const [startIndex, setStartIndex] = useState(0)
  const [endIndex, setEndIndex] = useState(
    Math.min(MAX_VISIBLE_ITEMS, dimensionsLength)
  )
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isLoadingRef = useRef(false)
  const previousStartIndexRef = useRef(0)
  const itemHeightRef = useRef<number | null>(null)

  useEffect(() => {
    setStartIndex(0)
    setEndIndex(Math.min(MAX_VISIBLE_ITEMS, dimensionsLength))
    previousStartIndexRef.current = 0
  }, [dimensionsLength])

  useEffect(() => {
    if (!itemHeightRef.current && scrollContainerRef.current) {
      const firstItem = scrollContainerRef.current.querySelector(
        '[data-instance-item]'
      ) as HTMLElement
      if (firstItem) {
        itemHeightRef.current = firstItem.offsetHeight + 16 // Add gap (theme.spacing(2))
      }
    }
  }, [endIndex])

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget
      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const clientHeight = container.clientHeight

      if (
        scrollHeight - scrollTop - clientHeight < 100 &&
        endIndex < dimensionsLength &&
        !isLoadingRef.current
      ) {
        isLoadingRef.current = true

        const previousStart = startIndex
        let newStartIndex = Math.min(
          startIndex + ITEMS_TO_REMOVE,
          dimensionsLength - MAX_VISIBLE_ITEMS
        )
        const newEndIndex = Math.min(endIndex + ITEMS_TO_ADD, dimensionsLength)

        const finalVisible = newEndIndex - newStartIndex
        if (finalVisible > MAX_VISIBLE_ITEMS) {
          newStartIndex = newEndIndex - MAX_VISIBLE_ITEMS
        }

        const itemsRemoved = newStartIndex - previousStart
        const scrollAdjustment = itemHeightRef.current
          ? itemsRemoved * itemHeightRef.current
          : 0

        setStartIndex(newStartIndex)
        setEndIndex(newEndIndex)
        previousStartIndexRef.current = newStartIndex

        requestAnimationFrame(() => {
          if (container && scrollAdjustment > 0) {
            container.scrollTop = scrollTop - scrollAdjustment
          }
          isLoadingRef.current = false
        })
      } else if (scrollTop < 100 && startIndex > 0 && !isLoadingRef.current) {
        isLoadingRef.current = true

        const previousStart = startIndex
        const newStartIndex = Math.max(startIndex - ITEMS_TO_ADD, 0)
        let newEndIndex = Math.max(
          endIndex - ITEMS_TO_REMOVE,
          newStartIndex + 1
        )

        const finalVisible = newEndIndex - newStartIndex
        if (finalVisible > MAX_VISIBLE_ITEMS) {
          newEndIndex = newStartIndex + MAX_VISIBLE_ITEMS
        }

        const itemsAdded = previousStart - newStartIndex
        const scrollAdjustment = itemHeightRef.current
          ? itemsAdded * itemHeightRef.current
          : 0

        setStartIndex(newStartIndex)
        setEndIndex(newEndIndex)
        previousStartIndexRef.current = newStartIndex

        requestAnimationFrame(() => {
          if (container && scrollAdjustment > 0) {
            container.scrollTop = scrollTop + scrollAdjustment
          }
          isLoadingRef.current = false
        })
      }
    },
    [startIndex, endIndex, dimensionsLength]
  )

  const visibleDimensions = dimensions.slice(startIndex, endIndex)

  return (
    <div data-testid="contract-instances-section">
      <BoxV4
        heading={'Instances'}
        headingIcon={
          <FontAwesomeIcon
            icon={faShelves}
            style={{ width: '18px', height: '14px' }}
          />
        }
      >
        {/* product dimenions */}
        <div className={classes.sectionContainer}>
          <div className={clsx(classes.root, classes.elementsGap)}>
            <Typography className={classes.totalCount}>
              Total: {dimensionsLength}
            </Typography>
            <div className={classes.titleWraper}>
              <Typography className={clsx(classes.sectionText)}>No.</Typography>
              <Typography
                className={clsx(classes.sectionText, classes.columnContent)}
              >
                Instance type
              </Typography>
              {values?.pricingModel !== 'usage-pricing' && (
                <Typography
                  className={clsx(classes.sectionText, classes.columnContent)}
                >
                  {flexiblePaymentTerms ? 'Quantity' : 'Price'}
                </Typography>
              )}
              <Typography
                className={clsx(classes.sectionText, classes.columnContent)}
              >
                Usage price/hr
              </Typography>
            </div>
            <div className={classes.border}></div>
            <div
              ref={scrollContainerRef}
              onScroll={handleScroll}
              className={clsx(
                classes.instancesContainer,
                classes.scrollableContainer
              )}
            >
              {visibleDimensions.map((dimension, visibleIndex) => {
                const actualIndex = startIndex + visibleIndex
                const originalIndex = actualIndex

                return (
                  <div
                    key={`dimension-${originalIndex}-${dimension.dimensionId}-${actualIndex}`}
                    data-instance-item
                    className={classes.instanceItem}
                  >
                    <div
                      className={classes.resourceRow}
                      data-testid="dimensions-row"
                    >
                      <div className={clsx(classes.indexColumn)}>
                        {actualIndex + 1}.
                      </div>
                      <div className={classes.resourcesContainer}>
                        <div
                          className={clsx(
                            classes.alignDescription,
                            classes.columnItems
                          )}
                          key={`dimensions-${randomKey}-label`}
                          data-testid="description-label"
                        >
                          {dimension.label}
                        </div>
                        {values?.pricingModel !== 'usage-pricing' && (
                          <div
                            className={clsx(
                              classes.alignDescription,
                              classes.columnItems
                            )}
                          >
                            <div
                              className={classes.fieldWidth}
                              key={`dimensions-${randomKey}-${getInputKey(
                                flexiblePaymentTerms
                              )}-${values?.pricingModel}-${actualIndex}`}
                            >
                              {flexiblePaymentTerms ? (
                                <TextInputV4
                                  keyName={`dimensions.${originalIndex}.quantity`}
                                  title={'Quantity'}
                                  type="number"
                                  placeHolder=""
                                  value={
                                    get(dimension, 'quantity', '') as number
                                  }
                                  updatedValue={
                                    get(dimension, 'quantity', '') as number
                                  }
                                  isError={getIsError(
                                    `dimensions.${originalIndex}.quantity`
                                  )}
                                  errorText={getErrorText(
                                    `dimensions.${originalIndex}.quantity`
                                  )}
                                  onChange={handleChange}
                                  isReadOnly={false}
                                  isRequired={false}
                                  showSpinner={false}
                                  onBlur={handleBlur}
                                  keyToPrevent={['+', '-', 'e']}
                                  alignRight
                                />
                              ) : (
                                <TextInputV4
                                  keyName={`dimensions.${originalIndex}.price`}
                                  title={'Enter price'}
                                  type="number"
                                  placeHolder=""
                                  value={get(dimension, 'price', '') as number}
                                  updatedValue={
                                    get(dimension, 'price', '') as number
                                  }
                                  isError={getIsError(
                                    `dimensions.${originalIndex}.price`
                                  )}
                                  errorText={getErrorText(
                                    `dimensions.${originalIndex}.price`
                                  )}
                                  onChange={handleChange}
                                  isReadOnly={false}
                                  isRequired={false}
                                  showSpinner={false}
                                  onBlur={handleBlur}
                                  keyToPrevent={['+', '-', 'e']}
                                  startAdornment={
                                    <InputStartAdornment
                                      text={
                                        getCurrencySymbol(
                                          values?.currency as string
                                        ) || '$'
                                      }
                                    />
                                  }
                                  alignRight
                                />
                              )}
                            </div>
                          </div>
                        )}
                        <div
                          key={`dimensions-${randomKey}-usage-price`}
                          className={clsx(
                            classes.alignDescription,
                            classes.columnItems
                          )}
                        >
                          <div className={classes.fieldWidth}>
                            <TextInputV4
                              keyName={`dimensions.${originalIndex}.additionalUsagePrice`}
                              title="Enter price"
                              type="number"
                              placeHolder=""
                              value={
                                get(
                                  dimension,
                                  'additionalUsagePrice',
                                  ''
                                ) as number
                              }
                              updatedValue={
                                get(
                                  dimension,
                                  'additionalUsagePrice',
                                  ''
                                ) as number
                              }
                              isError={getIsError(
                                `dimensions.${originalIndex}.additionalUsagePrice`
                              )}
                              errorText={getErrorText(
                                `dimensions.${originalIndex}.additionalUsagePrice`
                              )}
                              onChange={handleChange}
                              isReadOnly={false}
                              isRequired={false}
                              showSpinner={false}
                              onBlur={handleBlur}
                              keyToPrevent={['+', '-', 'e']}
                              startAdornment={
                                <InputStartAdornment
                                  text={
                                    getCurrencySymbol(
                                      values?.currency as string
                                    ) || '$'
                                  }
                                />
                              }
                              alignRight
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {dimensionsLength !== actualIndex + 1 ? (
                      <div className={classes.border}></div>
                    ) : (
                      <></>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </BoxV4>
    </div>
  )
}
